import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { IsfModel } from '@data-layer/models/isf';
import { enumIsfState } from '@data-layer/enums/isf';
import { ManagementEntityEventModel } from '@data-layer/models/management';
import { WidgetsService } from '@services/widgets.service';
import { EntityModel } from '@data-layer/models/entities';
import { IsfStateService } from '@shared/isf-mutual/services';
import { CompaniesService } from '@services/companies.service';
import { DocumentsService } from '@services/documents.service';
import { take, takeUntil } from 'rxjs/operators';
import { IsfCBPMessagesService } from '@shared/cbp-messages/services';
import { enumPaymentState } from '@data-layer/enums/payments';
import { TranslateService } from '@ngx-translate/core';
import { OrganizationsService } from '@services/organizations.service';
import { enumCompanyPaymentType } from '@app/data-layer/enums/companies';
import { enumCompanyPlacement } from '@enum/enumCompanyPlacement';
import { MarineInsuranceAvailabilityService } from '@shared/marine-insurance-shared/services';

@Component({
  selector: 'desktop-isf-summary-success-header',
  templateUrl: './desktop-summary-success-header.component.html',
  styleUrls: ['./desktop-summary-success-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesktopSummarySuccessHeaderComponent implements OnInit, OnDestroy {
  @Input() public isf: IsfModel;
  @Input() public cbpLogMessages: ManagementEntityEventModel[];
  @Input() public consolidator: EntityModel;
  @Input() public staffingLocation: EntityModel;
  @Input() public isEditMode: boolean;
  @Input() public isSent: boolean;
  @Input() public isCreating: boolean;
  @Input() public isShowEditButton: boolean = true;
  @Input() public isShowDuplicateButton: boolean = true;
  @Input() public isShowCreateEsButton: boolean = true;
  @Input() public isShowInsuranceIcon: boolean = false;
  @Output() public edit: EventEmitter<void> = new EventEmitter<void>();
  @Output() public duplicate: EventEmitter<void> = new EventEmitter<void>();
  @Output() public print: EventEmitter<void> = new EventEmitter<void>();
  @Output() public log: EventEmitter<void> = new EventEmitter<void>();
  @Output() public createES: EventEmitter<void> = new EventEmitter<void>();
  @Output() public closeNotification: EventEmitter<void> = new EventEmitter<void>();
  @Output() public addInsurance: EventEmitter<void> = new EventEmitter<void>();

  public enumCompanyPlacement: typeof enumCompanyPlacement = enumCompanyPlacement;
  public isCBPOpen: boolean;
  public messages: any[] = [];
  public isShowLoader: boolean;
  public showPoaRequiredModal: boolean = false;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private widgetsService: WidgetsService,
    private isfStateService: IsfStateService,
    private organizationsService: OrganizationsService,
    private companiesService: CompaniesService,
    private cbpMessagesService: IsfCBPMessagesService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private documentsService: DocumentsService,
    private router: Router,
    private marineInsuranceAvailabilityService: MarineInsuranceAvailabilityService,
  ) {}

  public onDuplicateClick(): void {
    this.documentsService
      .canActWithoutPoaPrompt$()
      .pipe(take(1))
      .subscribe((canProceed: boolean) => {
        if (canProceed) {
          this.duplicate.emit();
        } else {
          this.showPoaRequiredModal = true;
          this.cdr.markForCheck();
        }
      });
  }

  public onPoaModalDiscard(): void {
    this.showPoaRequiredModal = false;
    this.cdr.markForCheck();
  }

  public onPoaModalGoToPoa(): void {
    const companyId: number = this.companiesService.getCompany()?.id;
    if (!companyId) {
      return;
    }
    this.router.navigate(['/companies', 'account-settings', 'company-view', companyId], {
      queryParams: { isEditViewOpen: true },
      fragment: 'power-of-attorney',
    });
  }

  public ngOnInit(): void {
    this.cbpMessagesService.isShowLoader$.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => {
      this.isShowLoader = val;
      // due to multiple places where service is called & ChangeDetectionStrategy.OnPush - we have to run it manually
      this.cdr.markForCheck();
    });
    this.cbpMessagesService.cbpLogMessages$.pipe(takeUntil(this.destroy$)).subscribe((val: ManagementEntityEventModel[]) => {
      this.cbpLogMessages = val;
      this.cdr.markForCheck();
    });
    this.cbpMessagesService.messages$.pipe(takeUntil(this.destroy$)).subscribe((val: string[]) => {
      this.messages = val;
      this.cdr.markForCheck();
    });

    this.cbpMessagesService.initData(this.isf);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get isDeletedStatus(): boolean {
    return [enumIsfState.DELETED, enumIsfState.CBP_DELETED].includes(this.isf.state);
  }

  public get summaryBodyText(): string {
    if (!this.isf) {
      return;
    }

    switch (this.isf.state) {
      case enumIsfState.DRAFT_CBY_V_SEND:
      case enumIsfState.VALIDATED_AT_PROVIDER:
      case enumIsfState.PROVIDER_SEND_PENDING:
        return 'STATUS_ISF.DESCRIPTION.AWAITING_RESPONSE';

      case enumIsfState.BOND_PENDING:
        return 'STATUS_ISF.DESCRIPTION.AWAITING_BOND_PURCHASE';

      case enumIsfState.DRAFT_CBP_PEND:
      case enumIsfState.ACCEPTED_CBP:
        return 'STATUS_ISF.DESCRIPTION.RECEIVED';

      case enumIsfState.NOT_VALID_AT_PROVIDER:
        return 'STATUS_ISF.DESCRIPTION.CORRECTIONS_NEEDED';

      case enumIsfState.NO_BILL_ON_FILE:
        return 'STATUS_ISF.DESCRIPTION.NO_BILL_ON_FILE';

      case enumIsfState.BILL_TYPE_NOT_MATCHED:
        return 'STATUS_ISF.DESCRIPTION.BILL_TYPE_NOT_MATCHED';

      case enumIsfState.REJECTED_CBP:
        return 'STATUS_ISF.DESCRIPTION.REJECTED_CBP';

      case enumIsfState.APPROVED:
        return 'STATUS_ISF.DESCRIPTION.APPROVED';

      case enumIsfState.CBP_DELETE_PENDING:
      case enumIsfState.CBP_DELETED:
        return 'STATUS_ISF.DESCRIPTION.CBP_DELETED';

      case enumIsfState.DELETED:
        return 'STATUS_ISF.DESCRIPTION.DELETED';

      case enumIsfState.UNRECOVERABLE:
        return 'STATUS_ISF.DESCRIPTION.UNRECOVERABLE';

      case enumIsfState.CUSTOM:
        return this.isf.customState?.description;

      default:
        return '';
    }
  }

  public get isShowSupportBtn(): boolean {
    if (!this.isf) {
      return;
    }

    switch (this.isf.state) {
      case enumIsfState.UNRECOVERABLE:
      case enumIsfState.NOT_VALID_AT_PROVIDER:
      case enumIsfState.NO_BILL_ON_FILE:
      case enumIsfState.BILL_TYPE_NOT_MATCHED:
      case enumIsfState.REJECTED_CBP:
        return true;

      default:
        return false;
    }
  }

  public openCBP(): void {
    this.isCBPOpen = !this.isCBPOpen;
    this.log.emit();
  }

  public contactSupport(): void {
    const { referenceNumber }: { referenceNumber: string } = this.isf;
    this.widgetsService.trackData('Contact team:', { referenceNumber });
    this.widgetsService.openWidget(['isf']);
  }

  public get isSuccessNotification(): boolean {
    return this.isShowSuccessNotification;
  }

  public get isWarningNotification(): boolean {
    return this.isEditMode || !this.consolidator || !this.staffingLocation;
  }

  public get isPaymentPending(): boolean {
    const currentOrganization = this.organizationsService.getOrganization();
    const currentCompany = this.companiesService.getCompany();
    const customsPaymentType = currentCompany?.customsPaymentType ?? currentOrganization?.customsPaymentType;

    return (
      (customsPaymentType === enumCompanyPaymentType.INSTANT && this.isf?.paymentState === enumPaymentState.PENDING) ||
      (customsPaymentType === enumCompanyPaymentType.MONTHLY &&
        this.isf?.paymentState === enumPaymentState.PENDING &&
        this.isf?.state !== enumIsfState.DRAFT_CBY_V_SEND)
    );
  }

  public get notificationHeader(): string {
    return this.isEditMode ? 'LABELS.EDIT_MODE' : '';
  }

  public get notificationHeaderAction(): string {
    return this.isEditMode ? 'BUTTON.EXIT_EDIT_MODE' : '';
  }

  public get notificationBody(): string {
    return this.isEditMode ? 'LABELS.EDIT_MODE_HINT' : this.isPaymentPending ? 'YOUR_FILING_IS_ON_HOLD' : 'LABELS.ISF_SENT_NOTIFICATION';
  }

  public get isShowNotification(): boolean {
    return this.isEditMode;
  }

  public get isShowSuccessNotification(): boolean {
    return (
      !this.isEditMode &&
      [enumIsfState.DRAFT_CBY_V_SEND, enumIsfState.VALIDATED_AT_PROVIDER, enumIsfState.PROVIDER_SEND_PENDING].includes(this.isf?.state)
    );
  }

  public get isShowEditIcon(): boolean {
    return this.isfStateService.isCanEdit(
      this.isf?.state,
      this.isf?.paymentState,
      this.companiesService.getCompany()?.customsPaymentType ?? this.organizationsService.getOrganization()?.customsPaymentType,
    );
  }

  public get isShowDuplicateIcon(): boolean {
    return [
      enumIsfState.NOT_SENT,
      enumIsfState.REJECTED_CBP,
      enumIsfState.ACCEPTED_CBP,
      enumIsfState.PROVIDER_SEND_PENDING,
      enumIsfState.NO_BILL_ON_FILE,
      enumIsfState.BILL_TYPE_NOT_MATCHED,
      enumIsfState.CBP_DELETE_PENDING,
      enumIsfState.CBP_DELETED,
      enumIsfState.DELETED,
    ].includes(this.isf?.state);
  }

  public get isShowInsuranceIndicator(): boolean {
    if (!this.isShowInsuranceIcon || !this.isf) {
      return false;
    }

    return this.marineInsuranceAvailabilityService.isShowIndicatorForIsf(this.isf);
  }

  public get isShowCreateESIcon(): boolean {
    return !!this.isf?.shipment?.id;
  }

  public showTranslatedKey(message: any): boolean {
    return this.cbpMessagesService.showTranslatedKey(message);
  }

  public registerMessage(message: any): string {
    return this.cbpMessagesService.registerMessage(message);
  }

  public getEntityTypeText(message: any): string {
    return this.cbpMessagesService.getEntityTypeText(message);
  }

  public get entityCsOrLgText(): string {
    const result: string[] = [];

    if (!this.isf?.shipment?.consolidator?.id) {
      result.push(`${this.translateService.instant('INFO.CS.TITLE_DESKTOP')}`);
    }

    if (!this.isf?.shipment?.stuffingLocation?.id) {
      result.push(`${this.translateService.instant('INFO.LG.TITLE')}`);
    }

    return result.join(' & ');
  }
}
